import React, { useState, useEffect, useCallback } from 'react';

const iconMap = {
  trending_up: '\u2191', heart: '\u2661', alert: '!', star: '\u2605', sparkle: '\u2728', chart: '\uD83D\uDCC8', money: '\uD83D\uDCB0', chat: '\uD83D\uDCAC', shield: '\uD83D\uDEE1', risk: '\u26A0',
};

const featureHelpers = {
  REVENUE_MANAGER: {
    howItWorks: 'Click "Generate Recommendations" in the Revenue Manager screen. AI analyzes last 90 days occupancy, ADR, RevPAR, booking pace by source, and day-of-week patterns to recommend optimal rates per room type per date with confidence levels.',
    screenFlow: 'AI Functions \u2192 Revenue Manager \u2192 Generate Recommendations \u2192 Accept / Reject per rate card',
    tips: [
      'Run daily before 10 AM for best results \u2014 AI uses last 90 days night audit data',
      'Accept updates the rate plan calendar directly \u2014 review recommendations before accepting',
      'Works best with at least 30 days of night audit history',
    ],
  },
  GUEST_SENTIMENT: {
    howItWorks: 'Click "Analyze Feedback" in the Guest Feedback screen. AI processes up to 30 unanalyzed feedback entries, extracting sentiment (Positive/Negative/Neutral), categorizing issues (Cleanliness, Staff, F&B, etc.), and flagging urgency by department.',
    screenFlow: 'Front Office \u2192 Guest Feedback \u2192 Analyze Feedback \u2192 View sentiment dashboard, top issues, problem rooms',
    tips: [
      'Encourage guests to leave feedback at checkout for richer data',
      'Review urgency alerts daily \u2014 flagged items need immediate department attention',
      'Dashboard shows 30-day trends; use it in weekly GM reviews',
    ],
  },
  NO_SHOW_PREDICTOR: {
    howItWorks: 'Click "Score Reservations" to scan next 7 days confirmed bookings. AI scores each 0\u2013100 based on advance payment ratio, guest history (past no-shows/cancellations), booking source reliability, lead time, and corporate flag.',
    screenFlow: 'AI Functions \u2192 No-Show Predictor \u2192 Score Reservations \u2192 View HIGH/MEDIUM/LOW risk cards \u2192 Take action (call to reconfirm)',
    tips: [
      'HIGH risk guests: call to reconfirm at least 24 hours before arrival',
      'Use with overbooking strategy \u2014 if many HIGH risk bookings, consider controlled oversell',
      'Risk badges also appear on Command Center room tiles for at-a-glance view',
    ],
  },
  GUEST_RECOGNITION: {
    howItWorks: 'Search a guest by mobile/email/name, then click "Generate Profile". AI builds a comprehensive profile from all touchpoints \u2014 stay history, folio spending (room, F&B, spa), feedback, service requests, and staff notes.',
    screenFlow: 'AI Functions \u2192 Guest Recognition \u2192 Search Guest \u2192 Generate Profile \u2192 View loyalty tier, preferences, special occasions, AI recommendations',
    tips: [
      'At check-in: search the guest to see preferences, allergies, VIP instructions before greeting',
      'At checkout: add staff notes (e.g., "prefers high floor", "anniversary next month") \u2014 these persist and improve future profiles',
      'During stay: log F&B preferences, room service patterns, and special requests for richer AI insights',
      'Profile is cached until guest data changes \u2014 no extra AI cost for repeat views',
    ],
  },
  HOUSEKEEPING_OPTIMIZER: {
    howItWorks: 'Click "Generate Priorities" in Housekeeping \u2192 AI Optimizer tab. AI ranks all rooms by departure time, VIP arrivals, turnaround windows, floor proximity, and staff availability to produce an optimized cleaning sequence.',
    screenFlow: 'Housekeeping \u2192 AI Optimizer tab \u2192 Generate Priorities \u2192 Accept individual / Accept All \u2192 Tasks auto-created',
    tips: [
      'Run after morning briefing when staff attendance is confirmed',
      'Accept All pushes the entire priority list as housekeeping tasks instantly',
      'VIP arrivals and tight turnaround rooms are always ranked highest',
      'Priority badges also show on Command Center room tiles',
    ],
  },
  OCCUPANCY_FORECAST: {
    howItWorks: 'Select forecast horizon (30/60/90 days) and click "Generate Forecast". AI projects daily occupancy %, ADR, RevPAR, and revenue using last 365 days history, current on-the-books inventory, and active reservations with confidence bands.',
    screenFlow: 'AI Functions \u2192 Occupancy Forecast \u2192 Select horizon \u2192 Generate Forecast \u2192 View KPIs, bar chart, daily table with room-type breakdown',
    tips: [
      'Use 30-day forecast for tactical pricing, 90-day for strategic planning',
      'Low-demand days flagged \u2014 consider promotions or OTA rate pushes',
      'High-demand days flagged \u2014 consider rate increases or minimum stay restrictions',
      'Compare Actual vs Forecast periodically to track AI accuracy',
    ],
  },
  UPSELL_ENGINE: {
    howItWorks: 'Click "Scan Guests" to analyze all currently checked-in guests. AI identifies upsell opportunities (room upgrade, F&B promo, late checkout, amenity packages) based on room availability, guest profile, spending history, and current occupancy level.',
    screenFlow: 'AI Functions \u2192 Upsell Engine \u2192 Scan Guests \u2192 View suggestion cards by trigger (Check-in / During Stay / Pre-Departure) \u2192 Accept / Decline',
    tips: [
      'At check-in: offer room upgrades when occupancy is below 70%',
      'During stay: suggest F&B packages or spa deals based on guest spending pattern',
      'Pre-departure: offer late checkout or next-stay discount to loyal guests',
      'Accepted upsells track actual revenue generated \u2014 use for staff incentives',
    ],
  },
  STAFF_ASSISTANT: {
    howItWorks: 'Type any question about hotel operations in natural language. AI responds using real-time data \u2014 today\'s occupancy, arrivals, departures, open HK tasks, last 14 days audit data. Data refreshes with each message.',
    screenFlow: 'AI Functions \u2192 Staff Assistant \u2192 Type question \u2192 Get instant answer with live data',
    tips: [
      'Quick queries: "What\'s occupancy today?", "List tomorrow\'s VIP arrivals", "How many open HK tasks?"',
      'Revenue queries: "ADR this month", "Revenue this week vs last week"',
      'Chat history preserved per user \u2014 scroll back to review previous answers',
      'Use quick-action chips for common questions without typing',
    ],
  },
  FOLIO_ANOMALY: {
    howItWorks: 'Click "Scan Folios" to check all open folios. AI detects rate mismatches (actual vs expected), duplicate postings, missing night charges, unexpected charges, and balance anomalies. Results are severity-coded: Critical, High, Medium, Low.',
    screenFlow: 'AI Functions \u2192 Folio Anomaly \u2192 Scan Folios \u2192 View anomaly cards with expected vs actual \u2192 Resolve / Dismiss',
    tips: [
      'Run daily before night audit to catch billing errors early',
      'Critical anomalies (red) indicate potential revenue leakage \u2014 address immediately',
      'Resolved/Dismissed actions are logged for audit trail',
      'Check "potential financial impact" value to prioritize which anomalies to fix first',
    ],
  },
  CORPORATE_CREDIT_RISK: {
    howItWorks: 'Click "Score All Corporates" to evaluate all active corporate accounts. AI analyzes credit utilization %, payment speed, overdue invoices, no-show rate, and booking trends over last 12 months to assign a risk score (0\u2013100).',
    screenFlow: 'AI Functions \u2192 Corporate Risk \u2192 Score All Corporates \u2192 View scorecard (GREEN/AMBER/RED) with credit utilization bars and recommendations',
    tips: [
      'RED corporates: consider reducing credit limit or requiring advance payment',
      'AMBER corporates: monitor closely, send payment reminders for overdue invoices',
      'Run monthly or before credit limit reviews',
      'Use risk factors breakdown to have informed conversations with corporate contacts',
    ],
  },
};

const helperLabelStyle = { fontSize: '0.72rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 };
const helperTextStyle = { fontSize: '0.82rem', color: '#334155', lineHeight: 1.5 };
const tipStyle = { fontSize: '0.8rem', color: '#334155', lineHeight: 1.5, padding: '2px 0 2px 0.5rem', borderLeft: '2px solid var(--theme-border)' };

function PmsHotelAiFeatures({ token, selectedProperty }) {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [expandedKey, setExpandedKey] = useState('');

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
  }), [token, selectedProperty]);

  const loadFeatures = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    try {
      const res = await fetch('/api/v1/ai/hotel-features', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setFeatures(data.features || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty?.id, headers]);

  useEffect(() => { loadFeatures(); }, [loadFeatures]);

  const toggleFeature = async (featureKey, newEnabled) => {
    setSaving(featureKey); setError(''); setMessage('');
    try {
      const res = await fetch('/api/v1/ai/hotel-features', {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify({ feature_key: featureKey, is_enabled: newEnabled }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setFeatures(prev => prev.map(f =>
        f.feature_key === featureKey ? { ...f, is_enabled_by_hotel: newEnabled } : f
      ));
      setMessage(`${featureKey.replace(/_/g, ' ')} ${newEnabled ? 'enabled' : 'disabled'}`);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving('');
    }
  };

  if (!selectedProperty?.id) {
    return (
      <div style={{ padding: '1rem', maxWidth: 800 }}>
        <h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>AI Features</h2>
        <p style={{ color: 'var(--theme-text-muted)' }}>Please select a property to manage AI features.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', maxWidth: 800 }}>
      <h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>AI Features</h2>
      <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
        Enable or disable AI-powered features for your property. Only features made available by the system administrator are shown below.
      </p>

      {error && <div className="error-banner" style={{ marginBottom: '0.5rem' }}>{error}</div>}
      {message && <div className="success-banner" style={{ marginBottom: '0.5rem' }}>{message}</div>}

      {loading ? (
        <div style={{ color: 'var(--theme-text-muted)', padding: '2rem', textAlign: 'center' }}>Loading AI features...</div>
      ) : features.length === 0 ? (
        <div style={{ background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '1.2rem 1.4rem', marginTop: '1.2rem' }}>
          <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--theme-text-muted)' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>No AI features available</div>
            <div style={{ fontSize: '0.85rem' }}>Contact your system administrator to enable AI features for this property.</div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {features.map((f) => {
            const enabled = f.is_enabled_by_hotel;
            const isSaving = saving === f.feature_key;
            const isExpanded = expandedKey === f.feature_key;
            const helper = featureHelpers[f.feature_key];
            return (
              <div key={f.feature_key} style={{
                background: '#fff', border: `1px solid ${enabled ? 'var(--theme-border)' : 'var(--theme-border-light)'}`,
                borderRadius: 10, overflow: 'hidden',
                opacity: isSaving ? 0.7 : 1, transition: 'opacity 0.2s',
              }}>
                {/* Main row */}
                <div style={{ padding: '1rem 1.2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {/* Icon */}
                  <div style={{
                    width: 42, height: 42, borderRadius: '50%',
                    background: enabled ? f.color || 'var(--theme-text-secondary)' : 'var(--theme-border)',
                    color: enabled ? '#fff' : 'var(--theme-text-muted)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.2rem', fontWeight: 700, flexShrink: 0,
                    transition: 'background 0.2s',
                  }}>
                    {iconMap[f.icon] || f.icon || '?'}
                  </div>

                  {/* Label + description + info toggle */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1e293b' }}>{f.label}</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--theme-text-secondary)', marginTop: 2 }}>{f.description}</div>
                    {helper && (
                      <button
                        onClick={() => setExpandedKey(isExpanded ? '' : f.feature_key)}
                        style={{
                          background: 'none', border: 'none', padding: 0, marginTop: 4,
                          fontSize: '0.78rem', color: '#3b82f6', cursor: 'pointer', fontWeight: 600,
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        {isExpanded ? '\u25B2 Hide guide' : '\u25BC How it works & tips'}
                      </button>
                    )}
                  </div>

                  {/* Toggle switch */}
                  <div style={{ flexShrink: 0 }}>
                    <button
                      onClick={() => toggleFeature(f.feature_key, !enabled)}
                      disabled={isSaving}
                      style={{
                        position: 'relative', width: 50, height: 28, borderRadius: 14,
                        border: 'none', cursor: isSaving ? 'wait' : 'pointer',
                        background: enabled ? '#22c55e' : '#cbd5e1',
                        transition: 'background 0.2s',
                        padding: 0,
                      }}
                      title={enabled ? 'Click to disable' : 'Click to enable'}
                    >
                      <span style={{
                        position: 'absolute', top: 3, left: enabled ? 25 : 3,
                        width: 22, height: 22, borderRadius: '50%',
                        background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        transition: 'left 0.2s',
                      }} />
                    </button>
                  </div>
                </div>

                {/* Expandable helper panel */}
                {isExpanded && helper && (
                  <div style={{
                    borderTop: '1px solid var(--theme-border-light)', background: 'var(--theme-bg-light)',
                    padding: '0.9rem 1.2rem 0.9rem 4.5rem',
                    display: 'flex', flexDirection: 'column', gap: '0.7rem',
                  }}>
                    <div>
                      <div style={helperLabelStyle}>How it works</div>
                      <div style={helperTextStyle}>{helper.howItWorks}</div>
                    </div>
                    <div>
                      <div style={helperLabelStyle}>Screen flow</div>
                      <div style={{ ...helperTextStyle, fontFamily: 'monospace', fontSize: '0.8rem', color: '#0f172a' }}>{helper.screenFlow}</div>
                    </div>
                    <div>
                      <div style={helperLabelStyle}>Recommendations</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {helper.tips.map((tip, i) => (
                          <div key={i} style={tipStyle}>{tip}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PmsHotelAiFeatures;
